import http from "@/http/http"
import { TRANSPOND_PREFIX } from "@/constants/config"
import type { Feature } from "@turf/turf"
import { deEncryptE3Coords } from "@/util/titleHelper"
import type { AreaItemVO, ModuleItemVO } from "@/api"

// 拼接项
const POSITION_SPLICE = "position"
const OPEN_SPLICE = "openapi/openapi"
const MAP_SPLICE = "map"

export interface FeijiteConf {
  // 区域权限
  area: AreaItemVO[]
  // 菜单
  modules: ModuleItemVO[]
  // lv,aoi,poi
  scenarioTypes: string[]
  // 模块是否可见
  visible: boolean
}

// 获取飞吉特权限
export async function getFeijiteConfig(): Promise<FeijiteConf> {
  try {
    const result = await http.get("/business/common/pgp/config", {
      params: {},
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface GetLVArgs {
  dt: string
  page: number
  accessList?: string[]
}

interface GetLVResult {
  location: number[][]
  page: number
}

// 获取多页lv数据
export async function getLvPoints(args: GetLVArgs): Promise<GetLVResult> {
  try {
    const { accessList, ...others } = args
    const params = accessList && accessList.length ? args : others
    const result = await http.post<GetLVResult>(
      `/${TRANSPOND_PREFIX}/${POSITION_SPLICE}/v2/api/geohash`,
      params,
      {
        cancelLoading: true,
      } as any,
    )
    return result as any
  } catch (error) {
    throw error
  }
}

interface GetGeolocationArgs {
  dt: string
  page: number
  lon: number
  lat: number
  radius: number
  accessList?: string[]
}

interface GetGeolocationResult {
  location: number[][]
  uv: number
}

// 获取定点范围数据数据
export async function getGeolocationPoints(
  args: GetGeolocationArgs,
): Promise<GetGeolocationResult> {
  try {
    const { accessList, ...others } = args
    const params = accessList && accessList.length ? args : others
    const result = await http.post<GetGeolocationResult>(
      `/${TRANSPOND_PREFIX}/${POSITION_SPLICE}/v2/api/geolocation`,
      params,
      {
        cancelLoading: true,
      } as any,
    )
    return result as any
  } catch (error) {
    throw error
  }
}

interface GetAoiArgs {
  lon: number
  lat: number
  radius: number
  tags?: string[]
}

interface CategoriesItemVO {
  id: string
  name: string
}

export interface GetAoiItemVO {
  id: string
  name: string
  lon: number
  lat: number
  city: {
    city_id: string
    city_name: string
  }
  polygon: number[][]
  categories: CategoriesItemVO[]
}

// 获取aoi数据
export async function getAoiPoints(args: GetAoiArgs): Promise<GetAoiItemVO[]> {
  try {
    const result = await http.post<GetAoiItemVO[]>(
      `/${TRANSPOND_PREFIX}/${OPEN_SPLICE}/v1/place_search/aoi`,
      args,
      {
        cancelLoading: true,
      } as any,
    )
    return result as any
  } catch (error) {
    throw error
  }
}

interface GetAoiTagAllArgs {
  id: string
  peopleType: number
}

interface PlaceItemVO {
  id: string
  name: string
  lon: number
  lat: number
  count: number
  distance: number
}

interface TagCountVO {
  id: string
  name: string
  tags: {
    id: string
    name: string
    count: number
  }[]
}

interface GetAoiTagAllResult {
  lon: number
  lat: number
  count: number
  residenceList: PlaceItemVO[]
  shoppingMallList: PlaceItemVO[]
  tagCountMap: TagCountVO[]
  workplaceList: PlaceItemVO[]
}

// 获取aoi画像
export async function getAoiTagAll(args: GetAoiTagAllArgs): Promise<GetAoiTagAllResult> {
  try {
    const result = await http.get(`/${TRANSPOND_PREFIX}/${OPEN_SPLICE}/v1/place_search/byId`, {
      params: args,
    })
    return result as any
  } catch (error) {
    throw error
  }
}

interface GetPoiArgs {
  lon: number
  lat: number
  radius: number
  tags?: string[]
}

interface TagItemVO {
  tag_id: string
  tagkey_id: string
}

export interface GetPoiItemVO {
  id: string
  name: string
  lon: number
  lat: number
  address: string
  city: {
    city_id: string
    city_name: string
  }
  categories: CategoriesItemVO[]
  tags: TagItemVO[]
}

// 获取poi数据
export async function getPoiPoints(args: GetPoiArgs): Promise<GetPoiItemVO[]> {
  try {
    const result = await http.post<GetPoiItemVO[]>(
      `/${TRANSPOND_PREFIX}/${OPEN_SPLICE}/v1/place_search/poi`,
      args,
      {
        cancelLoading: true,
      } as any,
    )
    return result as any
  } catch (error) {
    throw error
  }
}

interface GetMallArgs {
  longitude: number
  latitude: number
}

interface FloorDetailVO {
  id: string
  name: string
  index: number
  altitude: number
}

export interface MallItemVO {
  id: string
  name: string
  parent_id: string
  has_aoi: string
  district_id: string
  address: string
  bounds: string[]
  center: string[]
  floors: string[]
  floors_details: FloorDetailVO[]
}

interface GetMallResult {
  city_id: string
  city_name: string
  malls: MallItemVO[]
}

// 获取mall数据
export async function getMallPoints(args: GetMallArgs): Promise<GetMallResult> {
  try {
    const result = await http.get(`/${TRANSPOND_PREFIX}/${MAP_SPLICE}/map/indoormaps/v1/malls`, {
      params: args,
    })
    return result as any
  } catch (error) {
    throw error
  }
}

interface GetAreaGeoArgs {
  mallId: string
  floor: string
}

interface GetAreaGeoResult {
  name: string
  type: string
  crs: {
    type: string
    properties: {
      name: string
    }
  }
  features: Feature[]
}

// 根据建筑楼层获取楼层面积geo
export async function getAreaGeoByFloor(args: GetAreaGeoArgs): Promise<GetAreaGeoResult> {
  try {
    const { mallId, floor } = args
    const result: any = await http.get(
      `/${TRANSPOND_PREFIX}/${MAP_SPLICE}/map/indoormaps/v1/${mallId}/${floor}.json`,
    )
    if (result) {
      // 面积经纬度解码
      result.features.forEach((item: any) => {
        const coordinates = item.geometry.coordinates || []
        if (coordinates.length && Array.isArray(coordinates[0])) {
          coordinates[0].forEach((i) => {
            deEncryptE3Coords(i)
          })
        }
      })
    }
    return result
  } catch (error) {
    throw error
  }
}

interface GetFloorPoiArgs {
  floor: string
  mall_id: string
}

export interface FloorPoiVO {
  id: string
  name: string
  phone: string
  tag: string
  time: string
  type: string
  addr: string
  coord: number[]
}

interface GetFloorPoiResult {
  pois: FloorPoiVO[]
}

// 获取楼层内的poi点
export async function getFloorPoiPoints(args: GetFloorPoiArgs): Promise<GetFloorPoiResult> {
  try {
    const result = await http.get(`/${TRANSPOND_PREFIX}/${MAP_SPLICE}/map/indoormaps/v1/pois`, {
      params: {
        ...args,
        ak: "wayz",
      },
    })
    return result as any
  } catch (error) {
    throw error
  }
}
